import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Input, InputNumber, Modal, Radio, Spin, Tag, Tooltip, message,
} from 'antd';
import styled from 'styled-components';

import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

import { copyFieldsApi } from '../../../api/presentationsApi';
import {
  ICopyField, ICopyListShape, ICopyPart,
} from '../../../types/models/presentations';

/**
 * Describe the `copy.*` lists a template draws, at import.
 *
 * A key in the `copy.` namespace is not a typo the way an unknown vocabulary
 * path is — the namespace is open, and the template is what declares the field
 * exists. But an undescribed list is still a problem: it reaches a writer with
 * no guidance and the co-writer with no prompt, so the deck is blocked until
 * somebody says what it is for.
 *
 * **One description per list, not per item.** `copy.floorplan.0.body` and
 * `.2.body` are the same field asked twice; describing each one produced
 * fifteen copies of one sentence and made importing a template feel like data
 * entry rather than a decision.
 *
 * **Declarations are global.** Templates that draw the same slide share the
 * copy on it — written once, used by all of them. Which is
 * also why the server never overwrites a list that already exists: two
 * templates sharing one is the intended case, two authors meaning different
 * things by it is the failure. Lists already described do not appear here.
 *
 * **Changing one is a separate, deliberate step** — `mode="edit"`, opened from
 * the template's Edit dialog. It loads the saved descriptions rather than
 * drafting from the deck, saves each changed list with a PATCH, and says which
 * other templates share each one, since the change reaches them too.
 *
 * See `COPY_FIELDS.md` in the gutenberg repo.
 */

const Body = styled.div`
  max-height: 62vh;
  overflow-y: auto;
  padding: 16px 8px;
  margin: -24px 0;

  .list {
    padding: 14px 0;
    //border-bottom: 1px solid #f0f0f0;
    margin-bottom: 24px;

    &:last-child { border-bottom: none; }

    code {
      background: #fafafa;
      padding: 1px 5px;
      border-radius: 2px;
      font-size: 18px;
    }

    .slides { color: #8c8c8c; font-size: 12px; margin-left: 6px; }

    .row {
      display: flex;
      gap: 10px;
      align-items: center;
      margin-top: 8px;
      flex-wrap: wrap;
    }

    .hint { color: #8c8c8c; font-size: 12px; margin-top: 6px; }

    .parts {
      margin-top: 8px;
      padding-left: 12px;
      border-left: 2px solid #f0f0f0;
    }

    .part { display: flex; gap: 8px; align-items: center; margin-top: 6px; }
    .part-name { font-size: 12px; width: 70px; color: #595959; }
  }
`;

export interface DeclareCopyFieldsModalProps {
  /** List roots with no declaration, from `undeclared_copy` problems. */
  keys: string[];
  /** What the deck asks of each list: slot count and which slots it can drop. */
  shapes?: Record<string, ICopyListShape>;
  /** Slide numbers per token, so somebody can go and look at the boxes. */
  slidesByKey?: Record<string, number[]>;
  /** Roots the deck draws that are already described, shown as reused. */
  reusedKeys?: string[];
  onDeclared?: (created: ICopyField[]) => void;
  /** `edit` loads the saved descriptions of `keys` and saves changes to them. */
  mode?: 'declare' | 'edit';
  /** The template being edited, left out of "also used by" in edit mode. */
  templateId?: number | string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
}

interface Draft {
  key: string;
  label: string;
  intent: string;
  kind: 'fixed' | 'range';
  parts: ICopyPart[];
  min_items: number;
  ai: 'draft' | 'never';
  /** Not sent — what the deck offers, so the form can bound `min_items`. */
  slots: number;
  /** Edit mode only: the saved declaration, and who else draws it. */
  id?: number;
  usedBy?: ICopyField['used_by'];
}

/** What the API is sent for one list; everything else in a draft is display. */
const payloadOf = ({ slots, id, usedBy, ...rest }: Draft) => rest;

/** A saved declaration as a draft. `slots` is still this deck's count. */
export const draftFromField = (field: ICopyField, shape?: ICopyListShape): Draft => ({
  key: field.key,
  label: field.label,
  intent: field.intent,
  kind: field.kind === 'range' ? 'range' : 'fixed',
  parts: field.parts ?? [],
  min_items: field.min_items,
  ai: field.ai ?? 'draft',
  slots: shape?.slots ?? Math.max(1, field.min_items),
  id: Number(field.id),
  usedBy: field.used_by,
});

/**
 * A starting point read from the deck itself.
 *
 * The kind is the useful inference: a deck that marks some slots optional is
 * describing a list whose length varies, and one that marks none is describing
 * a list that is always full. That is exactly the fixed-vs-range distinction,
 * and the author already made it when they authored the shapes — so asking
 * again would be asking them to repeat themselves.
 */
/**
 * Parts in the order a writer fills them: headings first, the rest as found.
 *
 * The deck's own order is not available — the scanner reports tokens sorted
 * alphabetically, which puts `body` above `title` — so this is only the
 * starting point. The form lets the author reorder, and the declaration keeps
 * whatever they chose.
 */
const HEADINGS = ['title', 'heading', 'subtitle'];
export const orderParts = (names: string[]) => [
  ...HEADINGS.filter((name) => names.includes(name)),
  ...names.filter((name) => !HEADINGS.includes(name)),
];

export const draftFor = (key: string, shape?: ICopyListShape): Draft => {
  const slots = shape?.slots ?? 1;
  const optional = shape?.optional ?? [];
  const ranged = optional.length > 0;
  const words = key.replace(/^copy\./, '').replace(/[._]/g, ' ');
  return {
    key,
    label: words.charAt(0).toUpperCase() + words.slice(1),
    intent: '',
    kind: ranged ? 'range' : 'fixed',
    parts: orderParts(shape?.parts ?? []).map((name) => ({ name, max_chars: null })),
    // A range list needs however many slots the deck refuses to drop; a fixed
    // list needs all of them.
    min_items: ranged ? Math.max(1, slots - optional.length) : slots,
    ai: 'draft',
    slots,
  };
};

const DeclareCopyFieldsModal = ({
  keys, shapes, slidesByKey, reusedKeys, onDeclared,
  mode = 'declare', templateId,
  open: openProp, onOpenChange, children,
}: DeclareCopyFieldsModalProps) => {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  };

  const editing = mode === 'edit';
  const [drafts, setDrafts] = useState<Draft[]>([]);
  // Edit mode: the drafts as loaded, so only lists that changed are saved.
  const [saved, setSaved] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Rebuilt whenever the set of keys changes — a second upload of a different
  // deck must not inherit the first one's half-written intents.
  useEffect(() => {
    if (editing) return;
    setDrafts(keys.map((key) => draftFor(key, shapes?.[key])));
    setShowErrors(false);
  }, [keys.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode reloads on every open, so it never shows a description someone
  // else has changed since, and a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (!editing || !open) return;
    let live = true;
    setShowErrors(false);
    setLoading(true);
    copyFieldsApi.list()
      .then((fields: ICopyField[]) => {
        if (!live) return;
        const byKey = new Map(fields.map((field) => [field.key, field]));
        const loaded = keys.flatMap((key) => {
          const field = byKey.get(key);
          return field ? [draftFromField(field, shapes?.[key])] : [];
        });
        setDrafts(loaded);
        setSaved(loaded);
      })
      .catch(() => message.error('Could not load the descriptions.'))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [editing, open, keys.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = useMemo(() => drafts.filter((draft) => {
    const before = saved.find((row) => row.key === draft.key);
    return !before
      || JSON.stringify(payloadOf(before)) !== JSON.stringify(payloadOf(draft));
  }), [drafts, saved]);

  const patch = (key: string, change: Partial<Draft>) =>
    setDrafts((prev) => prev.map(
      (draft) => (draft.key === key ? { ...draft, ...change } : draft)));

  const patchPart = (key: string, name: string, change: Partial<ICopyPart>) =>
    setDrafts((prev) => prev.map((draft) => (draft.key === key
      ? {
        ...draft,
        parts: draft.parts.map(
          (part) => (part.name === name ? { ...part, ...change } : part)),
      }
      : draft)));

  /** Move a part up (-1) or down (+1). The order is how the editor shows them. */
  const movePart = (key: string, from: number, step: number) =>
    setDrafts((prev) => prev.map((draft) => {
      const to = from + step;
      if (draft.key !== key || to < 0 || to >= draft.parts.length) return draft;
      const parts = [...draft.parts];
      [parts[from], parts[to]] = [parts[to], parts[from]];
      return { ...draft, parts };
    }));

  const missingIntent = useMemo(
    () => drafts.filter((draft) => !draft.intent.trim()),
    [drafts]
  );

  const submit = async () => {
    // An intent is the only required answer, because it is the only one nothing
    // else can supply: it is what the writer reads and what the co-writer is
    // told. Everything else is inferred from the deck or has a default.
    if (missingIntent.length) {
      setShowErrors(true);
      message.warning(
        `${missingIntent.length} list${missingIntent.length === 1 ? '' : 's'} `
        + 'still need a description.');
      return;
    }
    setSaving(true);
    if (editing) {
      try {
        const updated = await Promise.all(changed.map(
          (draft) => copyFieldsApi.update(draft.id!, payloadOf(draft))));
        message.success(`Saved ${updated.length} list${
          updated.length === 1 ? '' : 's'}.`);
        onDeclared?.(updated);
        setOpen(false);
      } catch (e: any) {
        message.error(e?.response?.data?.detail ?? 'Could not save those changes.');
      } finally {
        setSaving(false);
      }
      return;
    }
    try {
      const { created } = await copyFieldsApi.declare(drafts.map(payloadOf));
      message.success(`Described ${created.length} list${
        created.length === 1 ? '' : 's'}.`);
      onDeclared?.(created);
      setOpen(false);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? 'Could not save those.');
    } finally {
      setSaving(false);
    }
  };

  /** Where in the deck a list lives, from any of its tokens. */
  const slidesFor = (root: string) => {
    const slides = new Set<number>();
    Object.entries(slidesByKey ?? {}).forEach(([token, numbers]) => {
      if (token === root || token.startsWith(`${root}.`)) {
        numbers.forEach((number) => slides.add(number));
      }
    });
    return Array.from(slides).sort((a, b) => a - b);
  };

  return (
    <>
      {children && <span onClick={() => setOpen(true)}>{children}</span>}
      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        width={780}
        title={editing ? "Edit this template's copy descriptions"
          : "Describe this template's copy"}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)}>Cancel</Button>,
          editing ? (
            <Button key="save" type="primary" loading={saving}
              disabled={loading || changed.length === 0} onClick={submit}>
              {changed.length === 0 ? 'No changes'
                : `Save ${changed.length} list${changed.length === 1 ? '' : 's'}`}
            </Button>
          ) : (
            <Button key="save" type="primary" loading={saving} onClick={submit}>
              Describe {drafts.length} list{drafts.length === 1 ? '' : 's'}
            </Button>
          ),
        ]}
      >

        {loading && (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        )}

        <Body>

          {editing ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="A change here applies to every template that draws the list"
              description={
                'Descriptions are shared, so every template drawing the same '
                + 'list reads the same guidance. Copy already written for properties '
                + 'is kept; the new wording is what the writer and the co-writer see '
                + 'from now on.'
              }
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="These will need to be hand-written or verified"
              description={
                'The template asks for text a person writes, per property. What you '
                + 'write here is the guidance whoever fills it reads, and the prompt '
                + 'the co-writer is given. Descriptions are shared by every template '
                + 'drawing the same list.'
              }
            />
          )}

          {(reusedKeys?.length ?? 0) > 0 && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${reusedKeys!.length} list${
                reusedKeys!.length === 1 ? ' is' : 's are'} already described`}
              description={
                <>
                  Reused as-is, so this deck shares their copy with the templates
                  that already draw them: <code>{reusedKeys!.join(', ')}</code>
                </>
              }
            />
          )}

          {showErrors && missingIntent.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${missingIntent.length} still need a description`}
            />
          )}


          {drafts.map((draft) => {
            const slides = slidesFor(draft.key);
            const others = (draft.usedBy ?? []).filter(
              (template) => String(template.id) !== String(templateId));
            return (
              <div className="list" key={draft.key}>
                <div>
                  <code>{draft.key}</code>
                  {slides.length > 0 && (
                    <span className="slides">
                      slide {slides.join(', ')}
                    </span>
                  )}
                  <Tag style={{ marginLeft: 8 }}>
                    {draft.slots} slot{draft.slots === 1 ? '' : 's'} in this deck
                  </Tag>
                  {others.length > 0 && (
                    <div className="hint">
                      Also used by {others.map((template) => template.name).join(', ')}
                    </div>
                  )}
                </div>

                <div className="row">
                  <Input
                    style={{ width: 260 }}
                    value={draft.label}
                    placeholder="Name shown to whoever writes it"
                    onChange={(e) => patch(draft.key, { label: e.target.value })}
                  />
                </div>

                <Input.TextArea
                  style={{ marginTop: 8 }}
                  rows={2}
                  status={showErrors && !draft.intent.trim() ? 'error' : ''}
                  value={draft.intent}
                  placeholder="What is this list for? Write it the way you would explain it to someone on the team."
                  onChange={(e) => patch(draft.key, { intent: e.target.value })}
                />

                <div className="row">
                  <Radio.Group
                    value={draft.kind}
                    size="small"
                    onChange={(e) => patch(draft.key, {
                      kind: e.target.value,
                      min_items: e.target.value === 'fixed'
                        ? draft.slots
                        : Math.min(draft.min_items, draft.slots),
                    })}
                  >
                    <Radio.Button value="fixed">Always {draft.slots}</Radio.Button>
                    <Radio.Button value="range">A range</Radio.Button>
                  </Radio.Group>

                  {draft.kind === 'range' && (
                    <Tooltip title={'The fewest items a property can get away '
                      + 'with. The most is whatever the deck has room for — the '
                      + 'unused blocks are removed rather than printed blank.'}>
                      <span className="row" style={{ margin: 0 }}>
                        <InputNumber
                          size="small"
                          min={1}
                          max={draft.slots}
                          value={draft.min_items}
                          onChange={(value) => patch(draft.key, {
                            min_items: (value as number) ?? 1,
                          })}
                        />
                        <span className="hint" style={{ marginTop: 0 }}>
                          to {draft.slots}
                        </span>
                      </span>
                    </Tooltip>
                  )}

                  <Radio.Group
                    value={draft.ai}
                    size="small"
                    onChange={(e) => patch(draft.key, { ai: e.target.value })}
                  >
                    <Radio.Button value="draft">AI may draft it</Radio.Button>
                    <Radio.Button value="never">A person must write it</Radio.Button>
                  </Radio.Group>
                </div>

                {draft.parts.length > 0 && (
                  <div className="parts">
                    <div className="hint" style={{ marginTop: 0 }}>
                      Each item has {draft.parts.length} parts, shown to the
                      writer in this order. A length limit is advisory — nothing
                      measures the real shape.
                    </div>
                    {draft.parts.map((part, index) => (
                      <div className="part" key={part.name}>
                        {draft.parts.length > 1 && (
                          <span>
                            <Button
                              type="text"
                              size="small"
                              aria-label={`Move ${part.name} up`}
                              icon={<ArrowUpOutlined />}
                              disabled={index === 0}
                              onClick={() => movePart(draft.key, index, -1)}
                            />
                            <Button
                              type="text"
                              size="small"
                              aria-label={`Move ${part.name} down`}
                              icon={<ArrowDownOutlined />}
                              disabled={index === draft.parts.length - 1}
                              onClick={() => movePart(draft.key, index, 1)}
                            />
                          </span>
                        )}
                        <span className="part-name">{part.name}</span>
                        <InputNumber
                          size="small"
                          min={1}
                          placeholder="max chars"
                          value={part.max_chars ?? undefined}
                          onChange={(value) => patchPart(draft.key, part.name, {
                            max_chars: (value as number) ?? null,
                          })}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {draft.ai === 'never' && (
                  <div className="hint">
                    The co-writer will not touch this, and a brochure cannot be
                    generated until somebody fills it in. Use it for facts we do
                    not hold anywhere — a unit&rsquo;s orientation, for instance
                    — where a plausible guess is worse than a blank.
                  </div>
                )}
              </div>
            );
          })}
        </Body>
      </Modal>
    </>
  );
};

export default DeclareCopyFieldsModal;
